/**
 * Lista de formularios — /comercial/formularios.
 *
 * EntityHeader + tabela (nome, tipo, status com toggle, respostas, link
 * publico copiavel quando ativo) + acoes (editar, duplicar, excluir). Criar
 * abre um SlideOverPanel pedindo nome + tipo.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCopy,
  FiExternalLink,
  FiFileText,
  FiList,
  FiPlus,
} from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  TIPOS_FORMULARIO,
  TIPO_FORMULARIO_LABEL,
  criarFormulario,
  duplicarFormulario,
  excluirFormulario,
  listarFormularios,
  atualizarFormulario,
  MSG_LINK_PELO_COCKPIT,
  tipoExigeConvite,
  urlPublicaFormulario,
} from "../../services/formularios";
import { PageContainer } from "../../styles/shared";

import { InlineIconButton, PublicLinkCell, Switch } from "./styles";

export default function Lista() {
  const { request, hasAllPermissions } = useAuth();
  // Criar, ativar, duplicar e excluir exigem formulario:manage no backend;
  // sem ela a tela so lista (FORM-41).
  const podeGerir = hasAllPermissions(["formulario:manage"]);
  const toast = useToast();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);

  // Criacao (SlideOverPanel)
  const [novoAberto, setNovoAberto] = useState(false);
  const [novoNome, setNovoNome] = useState("");
  const [novoTipo, setNovoTipo] = useState(TIPOS_FORMULARIO[0].value);
  const [erroNovo, setErroNovo] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const data = await listarFormularios(request);
      const lista = Array.isArray(data) ? data : data?.results || [];
      setItems(lista);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar formularios.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const kpis = useMemo(() => {
    const ativos = items.filter((f) => f.ativo).length;
    const respostas = items.reduce(
      (s, f) => s + (Number(f.total_respostas) || 0),
      0
    );
    return [
      { label: "Formularios", value: items.length },
      {
        label: "Ativos",
        value: ativos,
        sub: ativos > 0 ? "aceitando respostas" : "nenhum publicado",
      },
      { label: "Respostas (total)", value: respostas },
    ];
  }, [items]);

  // ---------- Criar ----------
  const abrirNovo = () => {
    setNovoNome("");
    setNovoTipo(TIPOS_FORMULARIO[0].value);
    setErroNovo("");
    setNovoAberto(true);
  };

  const criar = async () => {
    setErroNovo("");
    if (!novoNome.trim()) {
      setErroNovo("Informe o nome do formulario.");
      return;
    }
    try {
      const criado = await criarFormulario(request, {
        nome: novoNome.trim(),
        tipo: novoTipo,
      });
      setNovoAberto(false);
      toast.success("Formulario criado.");
      // Vai direto para o editor do recem-criado.
      if (criado?.id) {
        navigate(`/comercial/formularios/${criado.id}`);
      } else {
        await carregar();
      }
    } catch (err) {
      setErroNovo(err?.message || "Erro ao criar formulario.");
      throw err;
    }
  };

  // ---------- Acoes por linha ----------
  const toggleAtivo = async (form) => {
    const novo = !form.ativo;
    // Update otimista (toggle responde na hora; reverte se falhar).
    setItems((prev) =>
      prev.map((f) => (f.id === form.id ? { ...f, ativo: novo } : f))
    );
    try {
      // silentError: o catch abaixo ja avisa; sem ele saiam dois toasts.
      await atualizarFormulario(request, form.id, { ativo: novo }, { silentError: true });
      toast.success(novo ? "Formulario ativado." : "Formulario desativado.");
    } catch (err) {
      setItems((prev) =>
        prev.map((f) => (f.id === form.id ? { ...f, ativo: form.ativo } : f))
      );
      toast.error(err?.message || "Não foi possível alterar o status.");
    }
  };

  const duplicar = async (form) => {
    await duplicarFormulario(request, form.id);
    await carregar();
  };

  const excluir = async (form) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Excluir o formulário "${form.nome}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    await excluirFormulario(request, form.id);
    await carregar();
  };

  const copiarLink = async (form) => {
    const url = urlPublicaFormulario(form.slug);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link público copiado.");
    } catch (_) {
      toast.info(url);
    }
  };

  return (
    <PageContainer>
      <EntityHeader
        title="Formularios"
        subtitle="Construa formularios publicos de captura de leads e pesquisas."
        primaryAction={
          podeGerir
            ? { label: "Novo formulario", icon: <FiPlus />, onClick: abrirNovo }
            : undefined
        }
      />

      <KpiStrip items={kpis} />

      {erro && (
        <div className="so-banner danger" role="alert">
          <span>{erro}</span>
        </div>
      )}

      {loading ? (
        <p className="muted" style={{ fontSize: 13 }}>
          Carregando...
        </p>
      ) : items.length === 0 ? (
        <div className="empty">
          <div className="art">
            <FiFileText size={40} aria-hidden="true" />
          </div>
          <p className="title">Nenhum formulario ainda</p>
          <p className="msg">
            {podeGerir
              ? "Crie seu primeiro formulario para capturar leads pelo site, redes sociais ou campanhas."
              : "Ainda não há formulários nesta unidade."}
          </p>
          {podeGerir && (
            <div className="actions">
              <button type="button" className="btn btn--primary" onClick={abrirNovo}>
                <FiPlus size={14} aria-hidden="true" /> Novo formulario
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="table-wrap">
          <table className="dtable">
            <colgroup>
              <col style={{ width: "28%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Tipo</th>
                <th>Status</th>
                <th>Respostas</th>
                <th>Link público</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {items.map((form) => {
                const url = urlPublicaFormulario(form.slug);
                return (
                  <tr key={form.id}>
                    <td title={form.nome}>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/comercial/formularios/${form.id}`)
                        }
                        style={{
                          background: "none",
                          border: 0,
                          padding: 0,
                          font: "inherit",
                          color: "var(--accent)",
                          fontWeight: 500,
                          cursor: "pointer",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <FiFileText size={13} aria-hidden="true" />
                        {form.nome}
                      </button>
                    </td>
                    <td>
                      <span className="badge neutral no-dot">
                        {TIPO_FORMULARIO_LABEL[form.tipo] || form.tipo}
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Switch
                          type="button"
                          $on={Boolean(form.ativo)}
                          onClick={() => toggleAtivo(form)}
                          disabled={!podeGerir}
                          title={podeGerir ? undefined : "Sem permissão para ativar ou desativar"}
                          role="switch"
                          aria-checked={Boolean(form.ativo)}
                          aria-label={`Formulario ${form.ativo ? "ativo" : "inativo"}`}
                        />
                        <span className="tiny muted">
                          {form.ativo ? "Ativo" : "Inativo"}
                        </span>
                      </span>
                    </td>
                    <td className="num">{form.total_respostas ?? 0}</td>
                    <td>
                      {tipoExigeConvite(form.tipo) ? (
                        // FORM-12: o link de enriquecimento e' pessoal e nasce
                        // no cockpit do lead — /f/{slug} puro so avisa.
                        <span className="tiny muted" title={MSG_LINK_PELO_COCKPIT}>
                          {MSG_LINK_PELO_COCKPIT}
                        </span>
                      ) : form.ativo && form.slug ? (
                        <PublicLinkCell>
                          <span className="url" title={url}>
                            /f/{form.slug}
                          </span>
                          <InlineIconButton
                            type="button"
                            onClick={() => copiarLink(form)}
                            title="Copiar link"
                            aria-label="Copiar link público"
                          >
                            <FiCopy aria-hidden="true" />
                          </InlineIconButton>
                          <InlineIconButton
                            as="a"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir página pública"
                            aria-label="Abrir página pública"
                          >
                            <FiExternalLink aria-hidden="true" />
                          </InlineIconButton>
                        </PublicLinkCell>
                      ) : (
                        <span className="tiny muted">
                          {form.slug ? "Inativo" : "—"}
                        </span>
                      )}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          gap: 4,
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            navigate(`/comercial/formularios/${form.id}/respostas`)
                          }
                          title="Ver respostas"
                          aria-label={`Respostas de ${form.nome}`}
                        >
                          <FiList size={13} aria-hidden="true" />
                        </button>
                        {podeGerir && (
                          <>
                          <AsyncButton
                            variant="secondary"
                            onClick={() => duplicar(form)}
                            successMessage="Formulario duplicado."
                            style={{ height: 28, padding: "4px 10px", fontSize: 12 }}
                          >
                            <FiCopy size={12} aria-hidden="true" /> Duplicar
                          </AsyncButton>
                          <AsyncButton
                            variant="danger"
                            onClick={() => excluir(form)}
                            silentSuccess
                            style={{ height: 28, padding: "4px 10px", fontSize: 12 }}
                          >
                            Excluir
                          </AsyncButton>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* SlideOverPanel — criar formulario */}
      <SlideOverPanel
        open={novoAberto}
        onClose={() => setNovoAberto(false)}
        title="Novo formulario"
        subtitle="Comece pelo nome e tipo — os campos você monta no editor."
        width="min(95vw, 480px)"
        footer={
          <>
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setNovoAberto(false)}
            >
              Cancelar
            </button>
            <AsyncButton variant="primary" onClick={criar} silentError>
              Criar e abrir editor
            </AsyncButton>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {erroNovo && (
            <div className="so-banner danger" role="alert">
              <span>{erroNovo}</span>
            </div>
          )}
          <div className="input-group">
            <label className="input-label" htmlFor="novo-form-nome">
              Nome do formulario
            </label>
            <input
              id="novo-form-nome"
              className="input"
              type="text"
              value={novoNome}
              maxLength={200}
              autoFocus
              onChange={(e) => setNovoNome(e.target.value)}
              placeholder="Ex: Captacao site — cozinhas"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  criar();
                }
              }}
            />
          </div>
          <div className="input-group">
            <label className="input-label" htmlFor="novo-form-tipo">
              Tipo
            </label>
            <select
              id="novo-form-tipo"
              className="select"
              value={novoTipo}
              onChange={(e) => setNovoTipo(e.target.value)}
            >
              {TIPOS_FORMULARIO.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="input-help">
              Captura e Campanha alimentam o CRM (criam um lead a cada
              resposta).
            </span>
          </div>
        </div>
      </SlideOverPanel>
    </PageContainer>
  );
}
